import { lazy, Suspense, type JSX, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from '@ui/features/auth/Login';
import { CreateEstimatePage } from '@ui/features/estimates/CreateEstimate';
import { UpdatePrompt } from '@client/pwa/UpdatePrompt';
import { AuthGate } from '@ui/layouts/AuthGate';
import { AppShell } from '@ui/layouts/AppShell';
import { BrandMark } from '@ui/components/BrandMark';
import { Spinner } from '@ui/components/Spinner';
import { useAuthStore } from '@client/stores/auth';

// platform_owner has no business; tenant-scoped pages (/, /estimates, /settings/*)
// would 400/403 server-side. Bounce them to /admin if they hit those URLs directly
// (bookmark, manual nav, post-login redirect).
function TenantOnly({ children }: { children: ReactNode }): JSX.Element {
  const isOwner = useAuthStore((s) => s.me?.user.role === 'platform_owner');
  if (isOwner) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

// business_admin only — used to lock the Team tab. business_user hitting the
// URL directly (bookmark, link from email) gets bounced to their own profile
// instead of seeing a server-side 403.
function AdminOnly({ children }: { children: ReactNode }): JSX.Element {
  const isAdmin = useAuthStore((s) => s.me?.user.role === 'business_admin');
  if (!isAdmin) return <Navigate to="/settings/profile" replace />;
  return <>{children}</>;
}

// First paint = create-estimate flow. Everything else loads on navigation.
const HistoryPage = lazy(() =>
  import('@ui/features/estimates/History').then((m) => ({ default: m.HistoryPage })),
);
const EstimateDetailPage = lazy(() =>
  import('@ui/features/estimates/EstimateDetail').then((m) => ({ default: m.EstimateDetailPage })),
);
const PublicSharePage = lazy(() =>
  import('@ui/features/share/PublicShare').then((m) => ({ default: m.PublicSharePage })),
);
const SettingsLayout = lazy(() =>
  import('@ui/layouts/SettingsLayout').then((m) => ({ default: m.SettingsLayout })),
);
const BusinessProfilePage = lazy(() =>
  import('@ui/features/settings/BusinessProfile').then((m) => ({
    default: m.BusinessProfilePage,
  })),
);
const UsersPage = lazy(() =>
  import('@ui/features/settings/Users').then((m) => ({ default: m.UsersPage })),
);
const PreferencesPage = lazy(() =>
  import('@ui/features/settings/Preferences').then((m) => ({ default: m.PreferencesPage })),
);
const CatalogPage = lazy(() =>
  import('@ui/features/settings/Catalog').then((m) => ({ default: m.CatalogPage })),
);
const MyProfilePage = lazy(() =>
  import('@ui/features/me/MyProfile').then((m) => ({ default: m.MyProfilePage })),
);
const AdminGate = lazy(() =>
  import('@ui/features/admin/AdminGate').then((m) => ({ default: m.AdminGate })),
);
const AdminLayout = lazy(() =>
  import('@ui/features/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const BusinessesPage = lazy(() =>
  import('@ui/features/admin/Businesses').then((m) => ({ default: m.BusinessesPage })),
);
const GlobalCatalogPage = lazy(() =>
  import('@ui/features/admin/GlobalCatalog').then((m) => ({ default: m.GlobalCatalogPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

function Pending(): JSX.Element {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-slate-400">
      <BrandMark size="md" />
      <Spinner size="md" label="Loading" />
    </div>
  );
}

function L({ children }: { children: JSX.Element }): JSX.Element {
  return <Suspense fallback={<Pending />}>{children}</Suspense>;
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-slate-900 focus:px-3 focus:py-2 focus:text-sm focus:text-white"
        >
          Skip to content
        </a>
        <UpdatePrompt />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/share/:token"
            element={
              <L>
                <PublicSharePage />
              </L>
            }
          />
          <Route element={<AuthGate />}>
            <Route element={<AppShell />}>
              <Route
                path="/"
                element={
                  <TenantOnly>
                    <CreateEstimatePage />
                  </TenantOnly>
                }
              />
              <Route
                path="/estimates"
                element={
                  <TenantOnly>
                    <L>
                      <HistoryPage />
                    </L>
                  </TenantOnly>
                }
              />
              <Route
                path="/estimates/:id"
                element={
                  <TenantOnly>
                    <L>
                      <EstimateDetailPage />
                    </L>
                  </TenantOnly>
                }
              />
              <Route
                path="/me/profile"
                element={
                  <TenantOnly>
                    <L>
                      <MyProfilePage />
                    </L>
                  </TenantOnly>
                }
              />
              <Route
                path="/settings"
                element={
                  <TenantOnly>
                    <L>
                      <SettingsLayout />
                    </L>
                  </TenantOnly>
                }
              >
                <Route index element={<Navigate to="profile" replace />} />
                <Route
                  path="profile"
                  element={
                    <L>
                      <BusinessProfilePage />
                    </L>
                  }
                />
                <Route
                  path="users"
                  element={
                    <AdminOnly>
                      <L>
                        <UsersPage />
                      </L>
                    </AdminOnly>
                  }
                />
                <Route
                  path="catalog"
                  element={
                    <L>
                      <CatalogPage />
                    </L>
                  }
                />
                <Route
                  path="preferences"
                  element={
                    <L>
                      <PreferencesPage />
                    </L>
                  }
                />
              </Route>
              <Route
                path="/admin"
                element={
                  <L>
                    <AdminGate>
                      <AdminLayout />
                    </AdminGate>
                  </L>
                }
              >
                <Route index element={<Navigate to="businesses" replace />} />
                <Route
                  path="businesses"
                  element={
                    <L>
                      <BusinessesPage />
                    </L>
                  }
                />
                <Route
                  path="catalog"
                  element={
                    <L>
                      <GlobalCatalogPage />
                    </L>
                  }
                />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
